/**
 * Whop checkout URL helper.
 *
 * Active 2-way A/B split (2026-05-15), deterministic per userId via
 * `userId % 100 < 50` (50/50, sticky-per-user):
 *
 *   Variant A (control)  plan_CVIJFFt87IXOz   $4.95   3-day trial → $14.95/wk
 *   Variant B            plan_C0vPKySnDmeA3   $3.95   24-hr trial → $19.95/2wk
 *
 * Variant B replaced the prior $19.95-upfront and $9.95 variants (both
 * produced 0 sales over 100 clicks in 7 days). Plan IDs are hardcoded
 * locally (no env-driven path) — the server mirror in checkoutAb.ts pulls
 * the same values from whopPlans.ts so a deploy can never put server and
 * client on different plans.
 *
 * Conversion attribution: every CTA tap calls `logCheckoutClick({ planId, ... })`
 * which inserts a row into `checkout_clicks` carrying the picked `plan_id`.
 * Funnel analysis after 50 sales:
 *
 *   SELECT plan_id, COUNT(*) AS clicks
 *   FROM checkout_clicks
 *   WHERE created_at > now() - interval '14 days'
 *   GROUP BY 1
 *   ORDER BY 2 DESC;
 *
 * Joined against subscriptions / revenue_events keyed on plan_id, the variant
 * with the best $/click after 50 total sales wins.
 *
 * Anonymous (pre-login) traffic with no userId falls through to a uniform
 * random draw on each call. This is intentional — landing-page visitors who
 * never sign in don't move the needle on the experiment.
 *
 * --- Attribution forwarding (added 2026-05-10) ---
 *
 * Whop checkout accepts arbitrary query params and surfaces them on the
 * payment webhook (`data.metadata.*`). When `tracking` is supplied,
 * `buildWhopCheckoutUrl` appends the inbound landing-page attribution
 * (utm_source/medium/campaign/term, fbclid, gclid, sub_id from Keitaro,
 * referrer) onto the outbound URL so we don't lose attribution at conversion.
 * `utm_content` is intentionally NOT forwarded here — checkoutTracking.ts
 * overwrites it with `click_<uuid>` for sale stitching.
 *
 * Bug history: prior to this change, the variant CTA opened Whop without any
 * landing-page UTMs, so checkout_clicks rows had every UTM column empty
 * (e.g. row ids 81/82 from the $9.95 sale on plan_CprPLkWKTqnnE on
 * 2026-05-10). Whop's postback to Keitaro relies on `sub_id` passthrough,
 * which was being dropped here.
 *
 * --- A/B plan-ID desync detection (added 2026-05-10) ---
 *
 * The api-server's `/subscription/create-checkout-session` returns a
 * `data.checkout_url` built from `WHOP_AB_VARIANT_*` env vars on the server.
 * The client-side A/B assignment (also from Vite env vars) is *supposed*
 * to agree because both sides use the same deterministic `userId % 3`
 * bucketing — but if the server env and client env drift (a deploy ships
 * one without the other), the user gets routed to a different plan than
 * the one we logged in `checkout_clicks`, silently corrupting attribution.
 * `detectAbPlanDesync()` compares the two and surfaces a warning when they
 * disagree. It does NOT change the URL the user is sent to — the server's
 * URL still wins, since the server has the authoritative env config — it
 * just gives us a signal so we can fix the env sync.
 */

import {
  pickCheckoutPlan,
  type PickCheckoutPlanResult,
} from '@workspace/checkout';
import { getTrackingData } from './pixel';

/**
 * Inbound attribution captured at lander load. Mirrors the shape returned
 * by `getTrackingData()` so callers can pass it straight through. Any null /
 * empty / undefined field is silently dropped.
 */
export interface CheckoutTracking {
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  utmTerm?: string | null;
  fbclid?: string | null;
  gclid?: string | null;
  /** Keitaro sub_id (NOT sub_id_1..5 — Whop forwards this verbatim into postback metadata). */
  keitaroSubid?: string | null;
  referrer?: string | null;
}

export interface BuildWhopCheckoutUrlParams {
  baseUrl: string;
  userId?: number | string | null;
  email?: string | null;
  source?: string | null;
  /**
   * Inbound landing-page attribution. When omitted, callers should still
   * pass `getTrackingData()` explicitly via wrapper components. Provided
   * here as an explicit parameter (not auto-read inside the function) so
   * the helper stays unit-testable without DOM/storage shims.
   */
  tracking?: CheckoutTracking;
}

const VARIANT_A_PLAN_ID = 'plan_CVIJFFt87IXOz';
const VARIANT_B_PLAN_ID = 'plan_C0vPKySnDmeA3';

export const DEFAULT_WHOP_CHECKOUT_URL =
  (import.meta.env?.VITE_WHOP_CHECKOUT_URL as string | undefined) ||
  `https://whop.com/checkout/${VARIANT_A_PLAN_ID}`;

/**
 * Map of CheckoutTracking field → outbound URL param name. `utm_content` is
 * deliberately absent: it's reserved for `click_<uuid>` stitching downstream.
 */
const TRACKING_PARAM_MAP: Array<[keyof CheckoutTracking, string]> = [
  ['utmSource', 'utm_source'],
  ['utmMedium', 'utm_medium'],
  ['utmCampaign', 'utm_campaign'],
  ['utmTerm', 'utm_term'],
  ['fbclid', 'fbclid'],
  ['gclid', 'gclid'],
  ['keitaroSubid', 'sub_id'],
  ['referrer', 'referrer'],
];

function appendIfMissing(url: string, key: string, value: string): string {
  // Cheap substring check — every Whop URL we build is well-formed and the
  // keys we touch are ASCII, so this is safe and avoids URL parsing cost on
  // the hot path. The key form `&key=` / `?key=` matches the start of every
  // search param.
  const probe = `${key}=`;
  if (url.includes(`?${probe}`) || url.includes(`&${probe}`)) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}${key}=${encodeURIComponent(value)}`;
}

export function buildWhopCheckoutUrl({
  baseUrl,
  userId,
  email,
  source,
  tracking,
}: BuildWhopCheckoutUrlParams): string {
  const url = baseUrl || DEFAULT_WHOP_CHECKOUT_URL;

  if (!url.includes('whop.com/checkout/')) return url;
  // Idempotency: if the URL has already been stamped with utm_email we treat
  // it as fully decorated and don't touch it again. This guards against
  // accidental double-builds (e.g. server pre-fills then frontend re-runs).
  if (url.includes('utm_email')) return url;

  // Step 1: stamp papacito identity (utm_papacitoid + utm_email) when both
  // are known. Without these we fall through to a source-only stamp below.
  let out: string;
  if (email && userId) {
    const sep = url.includes('?') ? '&' : '?';
    out = `${url}${sep}utm_papacitoid=${encodeURIComponent(String(userId))}&utm_email=${encodeURIComponent(email)}`;
  } else {
    out = url;
  }

  // Step 2: explicit `source` arg (call-site label like 'swipe_limit_modal')
  // is more specific than the inbound utm_source from the lander, so it wins.
  // We only emit utm_source once.
  if (source) {
    out = appendIfMissing(out, 'utm_source', source);
  }

  // Step 3: forward inbound landing-page attribution. Whop passes these
  // through to its postback metadata; Keitaro reads `sub_id` server-side
  // to stitch the conversion to its click. Skipping this is exactly how
  // the Variant C bug (rows 81/82 with empty UTMs) was introduced.
  if (tracking) {
    for (const [field, paramName] of TRACKING_PARAM_MAP) {
      const v = tracking[field];
      if (typeof v !== 'string' || v.length === 0) continue;
      out = appendIfMissing(out, paramName, v);
    }
  }

  return out;
}

/**
 * Convenience: same as `buildWhopCheckoutUrl` but pulls inbound tracking
 * from `getTrackingData()` (localStorage + sessionStorage bundle written
 * by the /install lander and the inline capture in index.html).
 *
 * Use this from any CTA call-site that doesn't already have the bundle in
 * scope. It's a thin wrapper so unit tests can keep calling
 * `buildWhopCheckoutUrl` with explicit `tracking` and stay deterministic.
 */
export function buildWhopCheckoutUrlWithTracking(
  params: Omit<BuildWhopCheckoutUrlParams, 'tracking'>,
): string {
  const tracking = getTrackingData();
  return buildWhopCheckoutUrl({ ...params, tracking });
}

/**
 * Pure helper: extract the plan_id slug from a Whop checkout URL.
 *
 * Returns the slug between `/checkout/` and the next `?` / `#` / end of
 * string, with any trailing query string stripped. Returns `null` for any
 * URL that isn't a Whop checkout URL (lets callers distinguish "no plan
 * extractable" from "extracted plan empty"). Exported so the desync check
 * is unit-testable without standing up Vitest/jsdom for a React component.
 */
export function extractPlanIdFromCheckoutUrl(url: string | null | undefined): string | null {
  if (!url || typeof url !== 'string') return null;
  const m = url.match(/\/checkout\/([^?#/]+)/);
  if (!m) return null;
  const slug = m[1].split('?')[0].split('#')[0];
  return slug.length > 0 ? slug : null;
}

/**
 * A/B plan-ID desync detector. Compares the plan_id baked into the
 * server-returned checkout URL against the plan_id the client picked
 * deterministically from its A/B assignment. Returns `null` when the two
 * agree (or when either side is missing — we don't warn in degraded
 * states), and a structured payload when they diverge.
 *
 * Pure: no `console.warn` here — the caller does the logging so tests can
 * stay deterministic.
 */
export function detectAbPlanDesync(input: {
  serverUrl: string | null | undefined;
  clientPlanId: string | null | undefined;
}): { serverPlanId: string; clientPlanId: string } | null {
  const serverPlanId = extractPlanIdFromCheckoutUrl(input.serverUrl);
  const clientPlanId = (input.clientPlanId ?? '').trim() || null;
  if (!serverPlanId || !clientPlanId) return null;
  if (serverPlanId === clientPlanId) return null;
  return { serverPlanId, clientPlanId };
}

export function getCheckoutBaseUrl(userId?: number | string | null): PickCheckoutPlanResult {
  return pickCheckoutPlan({
    userId,
    controlPlanId: VARIANT_A_PLAN_ID,
    variantPlanId: VARIANT_B_PLAN_ID,
    ratio: 0.5,
  });
}
